// =====================================================================================
// FILE: src/components/ui/PhaseHeader.jsx
// Phase header with entitlements tooltip for MatchFlow Builder
// =====================================================================================

import React, { useState } from "react";
import { getPhaseTooltip } from "../../constants/entitlements";

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    marginTop: 32,
    marginBottom: 16,
    paddingBottom: 8,
    borderBottom: "3px solid #E91E63",
    position: "relative",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 1000,
    color: "#1e293b",
    letterSpacing: 0.5,
  },
  infoIcon: {
    width: 18,
    height: 18,
    color: "#9ca3af",
    cursor: "help",
    transition: "color 0.15s",
    flexShrink: 0,
  },
  infoIconHover: {
    color: "#2fd6b5",
  },
  tooltip: {
    position: "absolute",
    left: 0,
    top: "calc(100% + 8px)",
    width: "100%",
    maxWidth: 500,
    background: "#0f1419",
    color: "#fff",
    padding: "12px 16px",
    borderRadius: 8,
    fontSize: 12,
    lineHeight: 1.5,
    zIndex: 100,
    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
    border: "1px solid #1e2732",
  },
  tooltipTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: "#2fd6b5",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 6,
  },
  tooltipContent: {
    color: "#e5e7eb",
  },
  tooltipArrow: {
    position: "absolute",
    top: -6,
    left: 20,
    width: 12,
    height: 12,
    background: "#0f1419",
    transform: "rotate(45deg)",
    border: "1px solid #1e2732",
    borderBottom: "none",
    borderRight: "none",
  },
};

// Info icon SVG
const InfoIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

// ============================================================
// COMPONENT
// ============================================================
export function PhaseHeader({ phase, theme }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [iconHovered, setIconHovered] = useState(false);
  
  const tooltipText = getPhaseTooltip(phase);
  const hasTooltip = tooltipText && tooltipText.length > 0;
  
  // Use theme colors if provided
  const borderColor = theme?.primary || "#E91E63";
  const textColor = theme?.text || "#1e293b";
  
  return (
    <div style={{ ...styles.container, borderBottomColor: borderColor }}>
      <div style={styles.header}>
        <h2 style={{ ...styles.title, color: textColor }}>
          {phase}
        </h2>
        {hasTooltip && (
          <span
            style={{
              ...styles.infoIcon,
              ...(iconHovered ? styles.infoIconHover : {}),
            }}
            onMouseEnter={() => {
              setIconHovered(true);
              setShowTooltip(true);
            }}
            onMouseLeave={() => {
              setIconHovered(false);
              setShowTooltip(false);
            }}
          >
            {InfoIcon}
          </span>
        )}
      </div>
      
      {/* Tooltip */}
      {showTooltip && hasTooltip && (
        <div style={styles.tooltip}>
          <div style={styles.tooltipArrow} />
          <div style={styles.tooltipTitle}>2026 Entitlements</div>
          <div style={styles.tooltipContent}>{tooltipText}</div>
        </div>
      )}
    </div>
  );
}

export default PhaseHeader;
